import { supabase } from '@/integrations/supabase/client';
import type { TaskAgenda } from '@/types/taskAgenda';
import { nextPayloadWithAutoSlot } from '@/services/taskAgenda/taskAgendaSchedule';

const DEFAULT_INTEGRATION_APP_URL = 'https://bd-crm-ai-insights.0j5zde.easypanel.host';
const APP_URL = (import.meta.env.VITE_CRM_AI_INSIGHTS_URL as string | undefined)?.trim() || DEFAULT_INTEGRATION_APP_URL;

function buildAppUrl(path: string) {
  return `${APP_URL.replace(/\/$/, '')}${path}`;
}

function buildImportBatchSize(agenda: TaskAgenda): number {
  const raw = agenda.payload?.orcamentosImportBatchSize;
  return typeof raw === 'number' && raw > 0 && raw <= 100 ? raw : 10;
}

export async function runAgendaOrcamentosIntranetSync(
  agenda: TaskAgenda,
  options?: { automaticSlotKey?: string }
): Promise<void> {
  const automaticSlotKey = options?.automaticSlotKey;
  const nextPayload = () => nextPayloadWithAutoSlot(agenda, automaticSlotKey);
  const itens = buildImportBatchSize(agenda);

  const { data: runRow, error: insErr } = await supabase
    .from('task_agenda_runs')
    .insert({
      agenda_id: agenda.id,
      status: 'running',
      summary: null,
      error_message: null,
    })
    .select('id')
    .single();

  if (insErr || !runRow?.id) {
    const msg = insErr?.message || 'insert sem id';
    throw new Error(`Não foi possível registrar a execução na agenda: ${msg}`);
  }

  const runId = runRow.id as string;

  try {
    const response = await fetch(buildAppUrl('/intranet/orcamentos/import-sync'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itens, status_sync: 'pendente' }),
    });
    const json = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(json?.message || 'Falha ao importar fila de orçamentos da Intranet.');
    }

    const total = Number(json?.total || 0);
    const inserted = Array.isArray(json?.results) ? json.results.filter((item: any) => item?.action === 'insert').length : 0;
    const updated = Array.isArray(json?.results) ? json.results.filter((item: any) => item?.action === 'update').length : 0;
    const syncOnly = Array.isArray(json?.results) ? json.results.filter((item: any) => item?.action === 'sync-only-concluded').length : 0;
    const summary = total === 0
      ? 'Nenhum orçamento pendente na fila da Intranet.'
      : `Fila ${total} · inseridos ${inserted} · atualizados ${updated} · concluídos só sync ${syncOnly}`;

    await supabase.from('task_agenda_runs').update({
      finished_at: new Date().toISOString(),
      status: 'success',
      summary,
      error_message: null,
    }).eq('id', runId);

    await supabase.from('task_agenda').update({
      last_run_at: new Date().toISOString(),
      last_run_status: 'success',
      last_run_summary: summary,
      payload: nextPayload(),
    }).eq('id', agenda.id);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    await supabase.from('task_agenda_runs').update({
      finished_at: new Date().toISOString(),
      status: 'error',
      summary: null,
      error_message: msg,
    }).eq('id', runId);

    await supabase.from('task_agenda').update({
      last_run_at: new Date().toISOString(),
      last_run_status: 'error',
      last_run_summary: msg,
      payload: nextPayload(),
    }).eq('id', agenda.id);

    throw e;
  }
}
